import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useLang } from '@/hooks/use-lang'
import { t } from '@/lib/i18n'
import { api } from '@/lib/api'
import { Wordmark } from '@/components/wordmark'

export function ResetPasswordPage() {
  useLang()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await api.post('/api/auth/reset-password', { token, password })
      setDone(true)
      // Brief success state, then send the user to sign in with the new password.
      setTimeout(() => navigate('/login'), 1500)
    } catch {
      setError(t('resetFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg)' }}>
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center">
          <img src="/logo.png" className="w-12 h-12 mx-auto mb-4" alt="WebhookLens" />
          <h1 className="text-xl font-semibold" style={{ color: 'var(--white)' }}><Wordmark /></h1>
          <p className="text-sm mt-1" style={{ color: 'var(--muted)' }}>{t('resetPasswordTitle')}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-6 rounded-xl" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}>
          {!token ? (
            <p className="text-sm p-3 rounded-lg" style={{ color: 'var(--red)', backgroundColor: 'color-mix(in srgb, var(--red) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 25%, transparent)' }}>
              {t('resetLinkInvalid')}
            </p>
          ) : done ? (
            <p className="text-sm p-3 rounded-lg text-center" style={{ color: 'var(--accent-ink, var(--accent))', backgroundColor: 'var(--accent-soft)', border: '1px solid color-mix(in srgb, var(--accent) 25%, transparent)' }}>
              {t('resetSuccess')}
            </p>
          ) : (
            <>
              {error && (
                <p className="text-sm p-3 rounded-lg" style={{ color: 'var(--red)', backgroundColor: 'color-mix(in srgb, var(--red) 12%, transparent)', border: '1px solid color-mix(in srgb, var(--red) 25%, transparent)' }}>
                  {error}
                </p>
              )}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--muted)' }}>{t('newPassword')}</label>
                <input type="password" required minLength={8} value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none transition"
                  style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                  onFocus={e => (e.target as HTMLInputElement).style.borderColor = 'var(--accent)'}
                  onBlur={e => (e.target as HTMLInputElement).style.borderColor = 'var(--border)'}
                />
              </div>
              <button type="submit" disabled={loading}
                className="btn-accent w-full py-2.5 rounded-lg text-sm font-medium"
              >
                {t('resetPassword')}
              </button>
            </>
          )}
          <p className="text-sm text-center" style={{ color: 'var(--muted)' }}>
            <Link to="/login" className="font-medium transition" style={{ color: 'var(--accent)' }}
              onMouseEnter={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent-bright)'}
              onMouseLeave={e => (e.currentTarget as HTMLElement).style.color = 'var(--accent)'}
            >
              {t('backToLogin')}
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
